import React, { useState } from 'react';
import { useMasterRoles } from '@/hooks/useMasterRoles';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, Users, Shield } from 'lucide-react';
import RolesList from './RolesList';
import RoleEditDialog from './RoleEditDialog';
import RoleDeleteDialog from './RoleDeleteDialog';
import { RoleWithPermissions } from '@/types/userRole';

const sortRoles = (roles: RoleWithPermissions[]) => {
  const order = ['masterAdmin', 'reseller', 'admin', 'manager', 'user'];
  return roles.slice().sort((a, b) => {
    const getRank = (role: RoleWithPermissions) => {
      const rn = role.name.toLowerCase();
      if (rn.includes('master')) return 0;
      if (rn.includes('reseller')) return 1;
      if (rn.includes('admin')) return 2;
      if (rn.includes('manager')) return 3;
      if (rn.includes('user')) return 4;
      return 99;
    };
    return getRank(a) - getRank(b);
  });
};

const RoleManagement: React.FC = () => {
  const { roles, isLoading, availablePermissions, addRole, updateRole, deleteRole } = useMasterRoles();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleWithPermissions | null>(null);
  const [deletingRole, setDeletingRole] = useState<RoleWithPermissions | null>(null);

  const handleAddRole = async (role: Omit<RoleWithPermissions, 'id' | 'userCount'>) => {
    await addRole(role);
    setShowAddDialog(false);
  };

  const handleEditRole = async (roleId: string, roleData: Partial<RoleWithPermissions>) => {
    await updateRole(roleId, roleData);
    setEditingRole(null);
  };

  const handleDeleteRole = async (roleId: string) => {
    try {
      await deleteRole(roleId);
      setDeletingRole(null);
    } catch (error) {
      // Error is already handled in the hook with toast
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Role Management</CardTitle>
            <CardDescription>
              Define roles and assign permissions to control feature access
            </CardDescription>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Role
          </Button>
        </CardHeader>
        <CardContent>
          <RolesList
            roles={sortRoles(roles)}
            isLoading={isLoading}
            onEdit={setEditingRole}
            onDelete={setDeletingRole}
          />
        </CardContent>
      </Card>

      {/* Add Role Dialog */}
      {showAddDialog && (
        <RoleEditDialog
          open={showAddDialog}
          title="Add New Role"
          description="Create a new role with specific permissions"
          availablePermissions={availablePermissions}
          onClose={() => setShowAddDialog(false)}
          onSave={handleAddRole}
          confirmText="Create Role"
        />
      )}

      {/* Edit Role Dialog */}
      {editingRole && (
        <RoleEditDialog
          open={!!editingRole}
          title="Edit Role"
          description="Modify role permissions and settings"
          role={editingRole}
          availablePermissions={availablePermissions}
          onClose={() => setEditingRole(null)}
          onSave={(roleData) => handleEditRole(editingRole.id, roleData)}
          confirmText="Update Role"
        />
      )}

      {/* Delete Role Dialog */}
      {deletingRole && (
        <RoleDeleteDialog
          open={!!deletingRole}
          role={deletingRole}
          onClose={() => setDeletingRole(null)}
          onConfirm={() => handleDeleteRole(deletingRole.id)}
        />
      )}
    </div>
  );
};

export default RoleManagement;
